import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { SearchBox } from '@/components/SearchBox';
import { UserInterestsQuickSearch } from '@/components/UserInterestsQuickSearch';
import { RecentSearches } from '@/components/RecentSearches';
import { useSettings } from '@/contexts/SettingsContext';
import { useAuthUser } from '@/hooks/useAuthUser';
import { Button } from '@/components/ui/button';
import { coverUrl } from '@/utils/coverImage';

export const HeroSection = () => {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const user = useAuthUser();
  const [backgroundImage, setBackgroundImage] = useState<string>('/images/hero-bg.avif');

  useEffect(() => {
    selectRandomBackgroundImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.hero_background_images]);

  const selectRandomBackgroundImage = () => {
    if (settings.hero_background_images && settings.hero_background_images.length > 0) {
      const randomIndex = Math.floor(Math.random() * settings.hero_background_images.length);
      const raw = settings.hero_background_images[randomIndex];
      setBackgroundImage(coverUrl(raw, { width: 1920, quality: 78, resize: 'cover' }) || raw);

    }
  };

  const overlayOpacity = (settings.hero_overlay_opacity || 40) / 100;

  const scrollToEvents = () => {
    const eventsSection = document.getElementById('events-section');
    if (eventsSection) {
      eventsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="relative">
      {/* Hero Top Zone */}
      <div className="relative z-10 overflow-hidden">
        {/* Background Image */}
        {backgroundImage && (
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${backgroundImage})` }}
          />
        )}
        
        {/* Overlay */}
        <div 
          className="absolute inset-0 bg-on-surface"
          style={{ opacity: overlayOpacity }}
        />
        
        {/* Soft bottom fade for legibility, no lines */}
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-on-surface/50 to-transparent pointer-events-none" aria-hidden="true" />

        <div className="relative container mx-auto px-4 pt-16 pb-24 md:pt-24 md:pb-32 lg:pt-32 lg:pb-40">
          <div className="max-w-2xl">
            <button
              type="button"
              onClick={scrollToEvents}
              className="group mb-6 inline-flex min-h-11 max-w-full items-center gap-3 rounded-md bg-white/10 py-1.5 pl-1.5 pr-4 text-left text-white shadow-ambient backdrop-blur-md transition-[background-color,scale] duration-150 ease-out [@media(hover:hover)]:hover:bg-white/15 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-on-surface/40"
            >
              <span className="shrink-0 rounded bg-primary-container px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary-foreground">
                {t('eventPass.new')}
              </span>
              <span className="flex min-w-0 flex-col leading-tight sm:flex-row sm:items-baseline sm:gap-2">
                <span className="font-serif text-base italic tabular-nums">{t('eventPass.heroRibbon')}</span>
                <span className="truncate text-xs text-white/80 sm:text-sm">{t('eventPass.heroRibbonSub')}</span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 transition-transform duration-150 [@media(hover:hover)]:group-hover:translate-x-0.5" aria-hidden="true" />
            </button>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 font-serif leading-[1.05] text-white [text-wrap:balance]">
              {t('search.heroTitle')}
            </h1>
            <p className="text-base md:text-lg mb-8 text-white/85 font-sans max-w-lg [text-wrap:pretty]">
              {t('search.heroDescription')}
            </p>
            <Button
              onClick={scrollToEvents}
              className="gradient-editorial text-white px-6 py-3 h-auto min-h-11 text-sm font-medium rounded-md group transition-transform duration-150 ease-out active:scale-[0.96]"
            >
              {t('search.exploreCTA')}
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </div>

      {/* Search Bridge Card */}
      <div className="relative z-20 container mx-auto px-4">
        <div className="-mt-10 md:-mt-12 bg-surface-container-lowest rounded-md shadow-float p-5 md:p-8 max-w-3xl mx-auto">
          <div className="mb-4">
            <h2 className="text-lg font-semibold font-serif text-on-surface">{t('search.homeTitle')}</h2>
            <p className="text-sm text-muted-foreground font-sans mt-1">{t('search.homeDescription')}</p>
          </div>
          <SearchBox />
          <RecentSearches />
          {user && <UserInterestsQuickSearch />}
        </div>
      </div>
    </div>
  );
};
